import { useQueryClient } from '@tanstack/react-query'
import {
  csx,
  IconArchive,
  IconCheck,
  IconCopySimple,
  IconPencil,
  IconXCircle,
  Menu,
  MenuButton,
  MenuItem,
  useMenuState,
  useToast,
} from '@vtex/admin-ui'
import { defineMessages, useIntl } from 'react-intl'

import { useNavigation } from '@vtex/raccoon-next'
import { usePromotionActivation } from 'hooks'
import { getRnbErrorMessageId } from 'shared'
import { Event, type ColumnParams, type SimplifiedPromotion } from 'typings'
import { useAnalytics } from 'use-analytics'
import { usePromotionsListState } from '../../usePromotionsListState'
import { usePromotionState } from './promotionState'

const messages = defineMessages({
  edit: { id: 'admin/promotions.utils.edit' },
  deactivate: { id: 'admin/promotions.list.actions.deactivate' },
  activate: { id: 'admin/promotions.list.actions.activate' },
  duplicate: { id: 'admin/promotions.list.actions.duplicate' },
  archive: { id: 'admin/promotions.list.actions.archive' },
  success: { id: 'admin/promotions.list.actions.activate.success' },
  error: { id: 'admin/promotions.list.actions.activate.error' },
  undo: { id: 'admin/promotions.list.actions.undo' },
})

type Props = {
  item: ColumnParams<SimplifiedPromotion>['item']
  onDuplicate?: (item: SimplifiedPromotion) => void
}

export function ActionsButton({ item, onDuplicate }: Props) {
  const { formatMessage } = useIntl()
  const { track } = useAnalytics()
  const showToast = useToast()
  const { navigate } = useNavigation()

  const queryClient = useQueryClient()

  const { update } = usePromotionActivation()

  const updatePromotionStatus = ({
    id,
    status,
    allowUndo = true,
  }: {
    id: string
    status: boolean
    allowUndo?: boolean
  }) => {
    allowUndo !== false &&
      track(Event.PROMOTION_STATUS_CHANGED, {
        type: item.type,
        active: status,
      })

    update(
      { id, active: status },
      {
        onSuccess: (response: Response) => {
          if (!response.ok) throw new Error()

          showToast({
            variant: 'positive',
            message: formatMessage(messages.success, { status }),
            action: allowUndo
              ? {
                  label: formatMessage(messages.undo),

                  onClick: () => {
                    track(Event.UNDO_PROMOTION_STATUS_CHANGE, {
                      type: item.type,
                      active: !status,
                    })

                    updatePromotionStatus({
                      id,
                      status: !status,
                      allowUndo: false,
                    })
                  },
                }
              : undefined,
          })

          queryClient.invalidateQueries({ queryKey: ['active-promotions'] })
        },
        onError: (error: unknown) => {
          const messageId = getRnbErrorMessageId(error)

          showToast({
            variant: 'critical',
            message: messageId
              ? formatMessage({ id: messageId })
              : formatMessage(messages.error, { status }),
          })
        },
      }
    )
  }

  const { archive } = usePromotionState()

  const {
    searchState: { value: searchTerm },
    filtersState,
  } = usePromotionsListState()

  const menuState = useMenuState()

  return (
    <>
      <MenuButton
        state={menuState}
        variant="neutralTertiary"
        onClick={(event) => event.stopPropagation()}
        className={csx({ float: 'right' })}
        labelHidden
      />
      <Menu state={menuState}>
        <MenuItem
          icon={<IconPencil />}
          label={formatMessage(messages.edit)}
          onClick={() => {
            track(Event.NAVIGATED_TO_PROMOTION, {
              type: item.type,
              status: item.status,
              archived: false,
              source: 'row-action-menu',
              appliedFilters: [
                ...(searchTerm ? ['textSearch'] : []),
                ...filtersState.filter((f) => f.values.length).map((f) => f.id),
              ],
            })

            // Do not explicitly navigate here. Just let the event propagate.
            // When it hits the row itself, it will navigate as usually.
          }}
        />
        <MenuItem
          icon={item.isActive ? <IconXCircle /> : <IconCheck />}
          label={formatMessage(
            item.isActive ? messages.deactivate : messages.activate
          )}
          onClick={(event) => {
            event.stopPropagation()
            updatePromotionStatus({
              id: item.id,
              status: !item.isActive,
            })
          }}
        />

        <MenuItem
          icon={<IconCopySimple />}
          label={formatMessage(messages.duplicate)}
          onClick={(event) => {
            event.stopPropagation()
            if (onDuplicate) {
              onDuplicate(item)
              return
            }

            track(Event.DUPLICATED_PROMOTION, {
              id: item.id,
              type: item.type,
              status: item.status,
              archived: false,
              source: 'promotion-list',
            })

            navigate(`/new?duplicate=${item.id}&type=${item.type}`)
          }}
        />

        <MenuItem
          icon={<IconArchive />}
          label={formatMessage(messages.archive)}
          onClick={(event) => {
            event.stopPropagation()
            archive(item.id)
          }}
        />
      </Menu>
    </>
  )
}
